import { useState, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  DragIndicator,
  Add,
  ScaleOutlined,
  Inventory2,
  Delete,
  Edit,
  Save,
  Undo,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { RecipeV2Detail, RecipeComponent, StockItemSearchResult, RecipeKind } from '../types';
import { applyRoundAndFormat } from '../../../utils';
import useRecipeV2Api from '../hooks/useRecipeV2Api';
import AllergenChips from '../../../components/shared/AllergenChips';

interface IngredientsTabProps {
  recipe: RecipeV2Detail;
  editable: boolean;
  currencyCode?: string;
  onComponentsChange?: (components: RecipeComponent[]) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onMetadataChange?: (partial: Partial<RecipeV2Detail>) => void;
}

interface TreeRowProps {
  component: RecipeComponent;
  depth: number;
  editable: boolean;
  editingId: number | null;
  editDraft: Partial<RecipeComponent> | null;
  formatCurrency: (v: number) => string;
  onStartEdit: (component: RecipeComponent) => void;
  onSaveEdit: () => void;
  onRevertEdit: () => void;
  onEditDraftChange: (field: string, value: number) => void;
  onRemove: (id: number) => void;
}

/**
 * Non-draggable recursive row for sub-recipe children. Children are rendered as flat
 * sibling <TableRow>s (toggled by `expanded`) rather than a nested <Table> inside a
 * <Collapse>/<TableCell> - the nested-table approach left stray blank rows in the grid.
 */
const TreeRow = ({ component, depth, editable, editingId, editDraft, formatCurrency, onStartEdit, onSaveEdit, onRevertEdit, onEditDraftChange, onRemove }: TreeRowProps) => {
  const { t } = useTranslation('recipeV2');
  const [expanded, setExpanded] = useState(false);
  const hasChildren = component.isRecipe && !!component.children?.length;
  const isEditing = editingId === component.id;

  return (
    <Fragment>
      <TableRow
        sx={{
          '&:hover': { bgcolor: 'action.hover' },
          bgcolor: depth > 0 ? `grey.${Math.min(50 + depth * 50, 200)}` : undefined,
        }}
      >
        <TableCell sx={{ width: 40, px: 0.5 }} />
        <TableCell sx={{ pl: depth * 3 }}>
          <Stack direction="row"
            alignItems="center"
            spacing={0.5}>
            {hasChildren ? (
              <IconButton size="small"
                onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}
            <Box>
              <Typography variant="textSM"
                fontWeight={500}>
                {component.componentDescription}
              </Typography>
              <Typography variant="textXS"
                color="text.secondary"
                fontFamily="monospace">
                {component.componentItemNumber}
              </Typography>
            </Box>
            {component.isRecipe && (
              <Chip label={t('build.subRecipe')}
                size="small"
                color="info"
                variant="outlined"
                sx={{ ml: 1 }} />
            )}
          </Stack>
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.quantity ?? component.quantity}
              onChange={(e) => onEditDraftChange('quantity', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 0.01, min: 0 }}
            />
          ) : (
            component.quantity.toFixed(3)
          )}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.weight ?? component.weight}
              onChange={(e) => onEditDraftChange('weight', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 1, min: 0 }}
            />
          ) : (
            component.weight.toFixed(0)
          )}
        </TableCell>
        <TableCell align="center">{component.uom}</TableCell>
        <TableCell align="right">{formatCurrency(component.costPrice)}</TableCell>
        <TableCell align="right">
          <Stack direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}>
            <Typography variant="textSM">{component.costContribution.toFixed(1)}%</Typography>
            <Box
              sx={{
                width: Math.max(component.costContribution, 4),
                height: 8,
                borderRadius: 1,
                bgcolor: component.costContribution > 30 ? 'error.main' : component.costContribution > 15 ? 'warning.main' : 'success.main',
              }}
            />
          </Stack>
        </TableCell>
        <TableCell>
          {component.allergens?.length ? (
            <AllergenChips allergens={component.allergens}
              size="small"
              spacing={0.5} />
          ) : null}
        </TableCell>
        <TableCell align="right">
          {editable && (
            <Stack direction="row"
              spacing={0.5}
              justifyContent="flex-end">
              {isEditing ? (
                <>
                  <IconButton size="small"
                    color="primary"
                    onClick={onSaveEdit}>
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    onClick={onRevertEdit}>
                    <Undo fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton size="small"
                    onClick={() => onStartEdit(component)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    color="error"
                    onClick={() => onRemove(component.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          )}
        </TableCell>
      </TableRow>
      {hasChildren && expanded && component.children!.map((child) => (
        <TreeRow
          key={child.id}
          component={child}
          depth={depth + 1}
          editable={editable}
          editingId={editingId}
          editDraft={editDraft}
          formatCurrency={formatCurrency}
          onStartEdit={onStartEdit}
          onSaveEdit={onSaveEdit}
          onRevertEdit={onRevertEdit}
          onEditDraftChange={onEditDraftChange}
          onRemove={onRemove}
        />
      ))}
    </Fragment>
  );
};

/** Sortable top-level row (supports drag-and-drop reordering) */
const SortableTreeRow = ({ component, editable, editingId, editDraft, formatCurrency, onStartEdit, onSaveEdit, onRevertEdit, onEditDraftChange, onRemove }: Omit<TreeRowProps, 'depth'>) => {
  const { t } = useTranslation('recipeV2');
  const [expanded, setExpanded] = useState(false);
  const hasChildren = component.isRecipe && !!component.children?.length;
  const isEditing = editingId === component.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: component.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Fragment>
      <TableRow ref={setNodeRef}
        style={style}
        sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
        <TableCell sx={{ width: 40, px: 0.5 }}>
          {editable && (
            <DragIndicator
              {...attributes}
              {...listeners}
              sx={{ color: 'text.disabled', fontSize: 18, cursor: 'grab' }}
            />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row"
            alignItems="center"
            spacing={0.5}>
            {hasChildren ? (
              <IconButton size="small"
                onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 28 }} />
            )}
            <Box>
              <Typography variant="textSM"
                fontWeight={500}>
                {component.componentDescription}
              </Typography>
              <Typography variant="textXS"
                color="text.secondary"
                fontFamily="monospace">
                {component.componentItemNumber}
              </Typography>
            </Box>
            {component.isRecipe && (
              <Chip label={t('build.subRecipe')}
                size="small"
                color="info"
                variant="outlined"
                sx={{ ml: 1 }} />
            )}
          </Stack>
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.quantity ?? component.quantity}
              onChange={(e) => onEditDraftChange('quantity', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 0.01, min: 0 }}
            />
          ) : (
            component.quantity.toFixed(3)
          )}
        </TableCell>
        <TableCell align="right">
          {isEditing ? (
            <TextField
              size="small"
              type="number"
              value={editDraft?.weight ?? component.weight}
              onChange={(e) => onEditDraftChange('weight', parseFloat(e.target.value) || 0)}
              sx={{ width: 80 }}
              inputProps={{ step: 1, min: 0 }}
            />
          ) : (
            component.weight.toFixed(0)
          )}
        </TableCell>
        <TableCell align="center">{component.uom}</TableCell>
        <TableCell align="right">{formatCurrency(component.costPrice)}</TableCell>
        <TableCell align="right">
          <Stack direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}>
            <Typography variant="textSM">{component.costContribution.toFixed(1)}%</Typography>
            <Box
              sx={{
                width: Math.max(component.costContribution, 4),
                height: 8,
                borderRadius: 1,
                bgcolor: component.costContribution > 30 ? 'error.main' : component.costContribution > 15 ? 'warning.main' : 'success.main',
              }}
            />
          </Stack>
        </TableCell>
        <TableCell>
          {component.allergens?.length ? (
            <AllergenChips allergens={component.allergens}
              size="small"
              spacing={0.5} />
          ) : null}
        </TableCell>
        <TableCell align="right">
          {editable && (
            <Stack direction="row"
              spacing={0.5}
              justifyContent="flex-end">
              {isEditing ? (
                <>
                  <IconButton size="small"
                    color="primary"
                    onClick={onSaveEdit}>
                    <Save fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    onClick={onRevertEdit}>
                    <Undo fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <IconButton size="small"
                    onClick={() => onStartEdit(component)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small"
                    color="error"
                    onClick={() => onRemove(component.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          )}
        </TableCell>
      </TableRow>
      {hasChildren && expanded && component.children!.map((child) => (
        <TreeRow
          key={child.id}
          component={child}
          depth={1}
          editable={editable}
          editingId={editingId}
          editDraft={editDraft}
          formatCurrency={formatCurrency}
          onStartEdit={onStartEdit}
          onSaveEdit={onSaveEdit}
          onRevertEdit={onRevertEdit}
          onEditDraftChange={onEditDraftChange}
          onRemove={onRemove}
        />
      ))}
    </Fragment>
  );
};


const IngredientsTab = ({ recipe, editable, currencyCode, onComponentsChange, onDirtyChange, onMetadataChange }: IngredientsTabProps) => {
  const { t } = useTranslation('recipeV2');
  const api = useRecipeV2Api();
  const [components, setComponents] = useState<RecipeComponent[]>(recipe.components);

  // Scaling state
  const [scaleDialogOpen, setScaleDialogOpen] = useState(false);
  const [targetPortions, setTargetPortions] = useState<number>(recipe.yieldQuantity);
  const [isScaled, setIsScaled] = useState(false);

  // Batch recipe -> stock conversion state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertQuantity, setConvertQuantity] = useState<number>(recipe.yieldQuantity);
  const [isConverting, setIsConverting] = useState(false);
  const [convertSuccessMessage, setConvertSuccessMessage] = useState<string | null>(null);

  // Add component state
  const [addComponentOpen, setAddComponentOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<StockItemSearchResult[]>([]);

  // Edit component state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<RecipeComponent> | null>(null);

  const formatCurrency = useCallback((value: number) => applyRoundAndFormat(value, currencyCode), [currencyCode]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setComponents((prev) => {
        const oldIndex = prev.findIndex((c) => c.id === active.id);
        const newIndex = prev.findIndex((c) => c.id === over.id);
        const reordered = arrayMove(prev, oldIndex, newIndex).map((c, i) => ({ ...c, sortOrder: i + 1 }));
        onComponentsChange?.(reordered);
        api.reorderComponents(recipe.id, reordered.map((c) => c.id)).catch(() => {});
        return reordered;
      });
    }
  }, [onComponentsChange, api, recipe.id]);

  // Edit handlers
  const handleStartEdit = useCallback((component: RecipeComponent) => {
    setEditingId(component.id);
    setEditDraft({ quantity: component.quantity, weight: component.weight });
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingId === null || !editDraft) return;
    setComponents((prev) => {
      const updateComponent = (items: RecipeComponent[]): RecipeComponent[] =>
        items.map((item) => {
          if (item.id === editingId) {
            return { ...item, quantity: editDraft.quantity ?? item.quantity, weight: editDraft.weight ?? item.weight };
          }
          if (item.children) {
            return { ...item, children: updateComponent(item.children) };
          }
          return item;
        });
      const updated = updateComponent(prev);
      onComponentsChange?.(updated);
      onDirtyChange?.(true);
      return updated;
    });
    // Persist the component update
    const comp = components.find((c) => c.id === editingId);
    if (comp) {
      api.updateComponent(recipe.id, editingId, {
        quantity: editDraft.quantity ?? comp.quantity,
        weight: editDraft.weight ?? comp.weight,
        volume: comp.volume,
        uom: comp.uom,
        sortOrder: comp.sortOrder,
      }).catch(() => {});
    }
    setEditingId(null);
    setEditDraft(null);
  }, [editingId, editDraft, onComponentsChange, onDirtyChange, api, recipe.id, components]);

  const handleRevertEdit = useCallback(() => {
    setEditingId(null);
    setEditDraft(null);
  }, []);

  const handleEditDraftChange = useCallback((field: string, value: number) => {
    setEditDraft((prev) => prev ? { ...prev, [field]: value } : { [field]: value });
  }, []);

  // Remove handler
  const handleRemoveComponent = useCallback((id: number) => {
    setComponents((prev) => {
      const updated = prev.filter((c) => c.id !== id).map((c, i) => ({ ...c, sortOrder: i + 1 }));
      onComponentsChange?.(updated);
      onDirtyChange?.(true);
      return updated;
    });
    api.removeComponent(recipe.id, id).catch(() => {});
  }, [onComponentsChange, onDirtyChange, api, recipe.id]);

  // Scaling
  const handleApplyScale = () => {
    const factor = targetPortions / recipe.yieldQuantity;
    setComponents((prev) =>
      prev.map((c) => ({
        ...c,
        quantity: parseFloat((c.quantity * factor).toFixed(4)),
        weight: Math.round(c.weight * factor),
      })),
    );
    setIsScaled(true);
    setScaleDialogOpen(false);
  };

  const handleResetScale = () => {
    setComponents(recipe.components);
    setIsScaled(false);
    setTargetPortions(recipe.yieldQuantity);
  };

  // Batch recipe -> stock conversion (e.g. a batch of dough/sauce held as an inventory item)
  const handleConvertToStock = async () => {
    setIsConverting(true);
    try {
      const result = await api.convertToStock(recipe.id, { quantity: convertQuantity, uom: recipe.yieldUom });
      setConvertSuccessMessage(t('batch.convertSuccess', { quantity: result.quantity, uom: result.uom, itemNumber: result.stockItemNumber }));
      setConvertDialogOpen(false);
    } catch { /* noop */ }
    setIsConverting(false);
  };

  // Add component (stock items and recipes/sub-recipes are both searchable and addable here)
  const handleSearchItems = async (value: string) => {
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const results = await api.searchStockItems(value);
      setSearchResults(results.filter((r) =>
        !components.some((c) => c.componentProductId === r.id)
        && !(r.isRecipe && r.id === recipe.id),
      ));
    } catch {
      setSearchResults([]);
    }
  };

  const handleAddItem = (item: StockItemSearchResult | null) => {
    if (!item) return;
    const newComponent: RecipeComponent = {
      id: Date.now(),
      recipeId: recipe.id,
      componentProductId: item.id,
      componentItemNumber: item.itemNumber,
      componentDescription: item.description,
      quantity: 1,
      weight: item.weightNet,
      volume: 0,
      sortOrder: components.length + 1,
      uom: item.uom,
      costPrice: item.costPrice,
      costContribution: 0,
      weightNet: item.weightNet,
      isRecipe: !!item.isRecipe,
      allergens: [],
      carbonFactor: null,
    };
    // Persist to API
    api.addComponent(recipe.id, {
      componentProductId: item.id,
      componentItemNumber: item.itemNumber,
      componentDescription: item.description,
      quantity: 1,
      weight: item.weightNet,
      volume: 0,
      uom: item.uom,
    }).then((saved) => {
      // Update the temp ID with server-assigned ID
      setComponents((prev) => prev.map((c) => c.id === newComponent.id ? { ...c, id: saved.id } : c));
    }).catch(() => {});
    setComponents((prev) => {
      const updated = [...prev, newComponent];
      onComponentsChange?.(updated);
      onDirtyChange?.(true);
      return updated;
    });
    setSearchInput('');
    setSearchResults([]);
    setAddComponentOpen(false);
  };

  return (
    <Stack spacing={2}>
      {/* Recipe meta + action bar - fields styled the same as the create wizard (TextField + built-in label) */}
      <Stack direction="row"
        spacing={3}
        alignItems="flex-start"
        flexWrap="wrap"
        sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
        <TextField
          label={t('build.yieldQuantity')}
          type="number"
          value={recipe.yieldQuantity}
          onChange={(e) => onMetadataChange?.({ yieldQuantity: Number(e.target.value) || 1 })}
          disabled={!editable}
          inputProps={{ min: 1 }}
          sx={{ width: 140 }}
        />
        <TextField
          label={t('build.yieldUom')}
          value={recipe.yieldUom}
          onChange={(e) => onMetadataChange?.({ yieldUom: e.target.value })}
          disabled={!editable}
          sx={{ width: 140 }}
        />
        <TextField
          label={t('build.totalCost')}
          value={formatCurrency(recipe.totalCost)}
          disabled
          sx={{ width: 160 }}
        />
        <TextField
          label={t('build.costPerPortion')}
          value={formatCurrency(recipe.costPerPortion)}
          disabled
          sx={{ width: 160 }}
        />
        <Box flex={1} />
        {editable && (
          <Stack direction="row"
            spacing={1}>
            {recipe.recipeType === RecipeKind.Batch && (
              <Button size="md"
                startIcon={<Inventory2 />}
                variant="secondary"
                onClick={() => { setConvertQuantity(recipe.yieldQuantity); setConvertDialogOpen(true); }}>
                {t('batch.convertToStock')}
              </Button>
            )}
            <Button size="md"
              startIcon={<ScaleOutlined />}
              variant="secondary"
              onClick={() => setScaleDialogOpen(true)}>
              {t('build.scale')}
            </Button>
            <Button size="md"
              startIcon={<Add />}
              variant="primary"
              onClick={() => setAddComponentOpen(true)}>
              {t('build.addComponent')}
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Scaled indicator */}
      {isScaled && (
        <Stack direction="row"
          spacing={1}
          alignItems="center">
          <Chip
            label={t('build.scaledFrom', { original: recipe.yieldQuantity, target: targetPortions })}
            size="small"
            color="info"
          />
          <Button size="md"
            variant="secondary"
            onClick={handleResetScale}>
            {t('build.scaleReset')}
          </Button>
        </Stack>
      )}

      {/* Component tree with DnD */}
      <DndContext sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <SortableContext items={components.map((c) => c.id)}
          strategy={verticalListSortingStrategy}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>{t('ingredient')}</TableCell>
                  <TableCell align="right">{t('build.qty')}</TableCell>
                  <TableCell align="right">{t('build.weight')}</TableCell>
                  <TableCell align="center">{t('build.uom')}</TableCell>
                  <TableCell align="right">{t('cost')}</TableCell>
                  <TableCell align="right">{t('build.costContribution')}</TableCell>
                  <TableCell>{t('allergensLabel')}</TableCell>
                  {editable && <TableCell align="right">{t('actions.title')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {components.map((component) => (
                  <SortableTreeRow
                    key={component.id}
                    component={component}
                    editable={editable}
                    editingId={editingId}
                    editDraft={editingId === component.id ? editDraft : null}
                    formatCurrency={formatCurrency}
                    onStartEdit={handleStartEdit}
                    onSaveEdit={handleSaveEdit}
                    onRevertEdit={handleRevertEdit}
                    onEditDraftChange={handleEditDraftChange}
                    onRemove={handleRemoveComponent}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>

      {/* Scale dialog */}
      <Dialog open={scaleDialogOpen}
        onClose={() => setScaleDialogOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>{t('build.scaleTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('build.scalePrompt')}</Typography>
          <TextField
            type="number"
            fullWidth
            label={t('build.portions')}
            value={targetPortions}
            onChange={(e) => setTargetPortions(Number(e.target.value) || 1)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScaleDialogOpen(false)}
            variant="secondary">{t('actions.cancel')}</Button>
          <Button onClick={handleApplyScale}
            variant="primary">{t('build.scaleApply')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add component dialog */}
      <Dialog open={addComponentOpen}
        onClose={() => setAddComponentOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle>{t('build.addComponent')}</DialogTitle>
        <DialogContent>
          <Typography variant="textSM"
            color="text.secondary"
            sx={{ mb: 1 }}>
            {t('build.addComponentHelp')}
          </Typography>
          <Autocomplete
            options={searchResults}
            getOptionLabel={(o) => `${o.itemNumber} — ${o.description}`}
            renderOption={(props, o) => (
              <Box component="li"
                {...props}
                key={`${o.isRecipe ? 'recipe' : 'stock'}-${o.id}`}>
                <Stack direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}>
                  <span>{o.itemNumber} — {o.description}</span>
                  {o.isRecipe && (
                    <Chip label={t('build.subRecipe')}
                      size="small"
                      color="info"
                      variant="outlined" />
                  )}
                </Stack>
              </Box>
            )}
            inputValue={searchInput}
            onInputChange={(_, value) => {
              setSearchInput(value);
              handleSearchItems(value);
            }}
            onChange={(_, value) => handleAddItem(value)}
            renderInput={(params) => (
              <TextField {...params}
                label={t('build.searchComponents')}
                sx={{ mt: 1 }}
              />
            )}
            noOptionsText={searchInput.length < 2 ? 'Type to search...' : 'No results'}
            value={null}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddComponentOpen(false)}
            variant="secondary">{t('actions.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Convert batch recipe to stock dialog */}
      <Dialog open={convertDialogOpen}
        onClose={() => setConvertDialogOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle>{t('batch.convertToStock')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t('batch.convertPrompt')}</Typography>
          <TextField
            type="number"
            fullWidth
            label={t('batch.quantity')}
            value={convertQuantity}
            onChange={(e) => setConvertQuantity(Number(e.target.value) || 1)}
            inputProps={{ min: 1 }}
            InputProps={{ endAdornment: recipe.yieldUom }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}
            variant="secondary">{t('actions.cancel')}</Button>
          <Button onClick={handleConvertToStock}
            variant="primary"
            disabled={isConverting}>
            {t('batch.convertConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={convertSuccessMessage !== null}
        autoHideDuration={5000}
        onClose={() => setConvertSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success"
          variant="filled"
          onClose={() => setConvertSuccessMessage(null)}>
          {convertSuccessMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default IngredientsTab;
